import React, { useState } from 'react';
import { Button, Input, Select, Space, Typography, Collapse, Row, Col, Divider, theme, Popover, InputNumber, Checkbox } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, PlusOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { ActionButtons } from './ActionButtons';
import { renderTerminologySystemSelect, renderTerminologySelect } from '../utils/renderHelpers';
import { DEFAULT_SNOMED_SYSTEM, DEFAULT_ANSWER, ANSWER_FIELD_TYPES, createChildAnswer, PARAMETRABLE_FIELD_TYPES } from '../utils/constants';
import { deepClone } from '../utils/helpers';
import DebouncedInput from './DebouncedInput';

// Default config for parametrable field types (in editor context)
const PARAMETRABLE_DEFAULTS = {
  scale: { min: 0, max: 10, step: 1, showMarks: true, minLabel: '', maxLabel: '', useCustomMarks: false, customMarks: [], maxPosition: 100 }
};

// Render settings popover content for parametrable types
const renderParametrableSettings = (type, config, onChange) => {
  if (type === 'scale') {
    const useCustomMarks = config.useCustomMarks || false;
    const customMarks = config.customMarks || [];
    
    const updateConfig = (updates) => onChange({ ...config, ...updates });
    
    const addMark = () => {
      const maxPos = config.maxPosition || 100;
      const newPos = customMarks.length > 0 ? Math.min(maxPos, Math.max(...customMarks.map(m => m.position)) + 25) : 0;
      updateConfig({ customMarks: [...customMarks, { position: newPos, label: '', value: '' }] });
    };
    
    const updateMark = (index, field, val) => {
      const updated = [...customMarks];
      updated[index] = { ...updated[index], [field]: val };
      updateConfig({ customMarks: updated });
    };
    
    const removeMark = (index) => {
      updateConfig({ customMarks: customMarks.filter((_, i) => i !== index) });
    };

    return (
      <Space direction="vertical" size={8} style={{ width: 280 }}>
        <Typography.Text strong style={{ fontSize: 12 }}>Scale Settings</Typography.Text>
        <Checkbox
          checked={useCustomMarks}
          onChange={(e) => updateConfig({ 
            useCustomMarks: e.target.checked,
            customMarks: e.target.checked && customMarks.length === 0 
              ? [{ position: 0, label: 'Min', value: '0' }, { position: config.maxPosition || 100, label: 'Max', value: String(config.maxPosition || 100) }] 
              : customMarks 
          })}
          style={{ fontSize: 12 }}
        >
          Use custom marks (ratings/hybrid)
        </Checkbox>
        {!useCustomMarks ? (
          <>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Min:</span>
              <InputNumber size="small" value={config.min} onChange={(v) => updateConfig({ min: v })} style={{ width: 60 }} />
              <Input size="small" value={config.minLabel || ''} onChange={(e) => updateConfig({ minLabel: e.target.value })} placeholder="Label" style={{ width: 70 }} />
            </Space>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Max:</span>
              <InputNumber size="small" value={config.max} onChange={(v) => updateConfig({ max: v })} style={{ width: 60 }} />
              <Input size="small" value={config.maxLabel || ''} onChange={(e) => updateConfig({ maxLabel: e.target.value })} placeholder="Label" style={{ width: 70 }} />
            </Space>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Step:</span>
              <InputNumber size="small" value={config.step} min={1} onChange={(v) => updateConfig({ step: v })} style={{ width: 60 }} />
            </Space>
            <Checkbox 
              checked={config.showMarks} 
              onChange={(e) => updateConfig({ showMarks: e.target.checked })}
              style={{ fontSize: 12 }}
            >
              Show marks
            </Checkbox>
          </>
        ) : (
          <>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Max Position:</span>
              <InputNumber size="small" value={config.maxPosition || 100} min={1} onChange={(v) => updateConfig({ maxPosition: v })} style={{ width: 60 }} />
            </Space>
            <Space size={8}>
              <span style={{ fontSize: 12 }}>Step:</span>
              <InputNumber size="small" value={config.step || 1} min={1} onChange={(v) => updateConfig({ step: v })} style={{ width: 60 }} />
            </Space>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>Pos (0-{config.maxPosition || 100}) | Label | Output</Typography.Text>
              {customMarks.map((mark, i) => (
                <Space key={i} size={4}>
                  <InputNumber size="small" value={mark.position} min={0} max={config.maxPosition || 100} onChange={(v) => updateMark(i, 'position', v)} style={{ width: 50 }} placeholder="Pos" />
                  <Input size="small" value={mark.label} onChange={(e) => updateMark(i, 'label', e.target.value)} style={{ width: 70 }} placeholder="Label" />
                  <Input size="small" value={mark.value} onChange={(e) => updateMark(i, 'value', e.target.value)} style={{ width: 60 }} placeholder="Value" />
                  <Button type="text" size="small" danger onClick={() => removeMark(i)} style={{ padding: '0 4px' }}>×</Button>
                </Space>
              ))}
              <Button type="dashed" size="small" onClick={addMark} block style={{ fontSize: 11 }}>+ Add Mark</Button>
            </Space>
          </>
        )}
      </Space>
    );
  }
  return null;
};

// Component types that require user-defined options
const TYPES_WITH_OPTIONS = ['select', 'radio'];

const { Text } = Typography;
const { Panel } = Collapse;

/**
 * Recursive Answer Editor — single component for all nesting depths.
 * Supports: standard answers, field components, separators, linebreaks, infinite children.
 *
 * @param {Object}   answer       - Answer data (read-only for rendering)
 * @param {number}   depth        - Nesting level (0 = top-level)
 * @param {number}   index        - Position among siblings
 * @param {number}   total        - Total siblings count
 * @param {Function} getNode      - (schema) => mutable ref to this answer node
 * @param {Function} updateSchema - Top-level schema updater
 * @param {Function} onDelete     - Remove this answer from parent
 * @param {Function} onClone      - Clone this answer (optional)
 * @param {Function} onMove       - Move this answer (optional)
 * @param {Array}    allSiblings  - All sibling answers (for conditional display, top-level only)
 */
export const AnswerEditor = ({
  answer,
  depth = 0,
  index,
  total,
  getNode,
  updateSchema,
  onDelete,
  onClone,
  onMove,
  allSiblings = [],
}) => {
  const { token } = theme.useToken();
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const isSeparator = answer.type === 'separator';
  const isLineBreak = answer.type === 'linebreak';
  const isVisualElement = isSeparator || isLineBreak;
  const isComponentType = !!answer.componentType;
  const hasOptions = TYPES_WITH_OPTIONS.includes(answer.componentType);
  const isParametrable = PARAMETRABLE_FIELD_TYPES.includes(answer.componentType);
  const isTopLevel = depth === 0;

  // Get current config for parametrable types
  const getConfig = () => {
    const defaults = PARAMETRABLE_DEFAULTS[answer.componentType] || {};
    return { ...defaults, ...(answer.config || {}) };
  };

  // Update config for parametrable types
  const updateConfig = (newConfig) => {
    updateSchema((s) => {
      const node = getNode(s);
      if (node) node.config = newConfig;
    });
  };

  // --- Generic updaters via getNode ---

  const updateField = (field, value) => {
    updateSchema((s) => {
      const node = getNode(s);
      if (node) node[field] = value;
    });
  };

  const switchMode = (mode) => {
    updateSchema((s) => {
      const node = getNode(s);
      if (!node) return;
      if (mode === 'component') {
        node.componentType = 'text';
        delete node.concept;
        delete node.conceptSystem;
        delete node.conceptCode;
        delete node.terminology;
      } else {
        delete node.componentType;
        node.concept = '';
        node.conceptSystem = DEFAULT_SNOMED_SYSTEM;
        node.conceptCode = '';
      }
    });
  };

  const handleTerminology = (terminologyObject) => {
    updateSchema((s) => {
      const node = getNode(s);
      if (!node) return;
      if (terminologyObject) {
        node.terminology = terminologyObject;
        node.conceptCode = terminologyObject.code;
        node.concept = terminologyObject.code;
        if (terminologyObject.display && !node.label) node.label = terminologyObject.display;
      } else {
        node.terminology = null;
        node.conceptCode = '';
        node.concept = '';
      }
    });
  };

  const handleSystem = (value) => {
    updateSchema((s) => {
      const node = getNode(s);
      if (!node) return;
      node.conceptSystem = value;
      node.conceptCode = '';
      node.concept = '';
      node.terminology = null;
    });
  };

  const addChild = () => {
    updateSchema((s) => {
      const node = getNode(s);
      if (!node) return;
      if (!node.children) node.children = [];
      // Use createChildAnswer to ensure globally unique ID
      const parentId = node.id || `answer_${Date.now()}`;
      node.children.push(createChildAnswer(parentId));
    });
  };

  const addOption = () => {
    updateSchema((s) => {
      const node = getNode(s);
      if (!node) return;
      if (!node.options) node.options = [];
      node.options.push({ label: '' });
    });
  };

  const removeOption = (optIndex) => {
    updateSchema((s) => {
      const node = getNode(s);
      if (node?.options) node.options.splice(optIndex, 1);
    });
  };

  const updateOption = (optIndex, value) => {
    updateSchema((s) => {
      const opt = getNode(s)?.options?.[optIndex];
      if (opt) opt.label = value;
    });
  };

  // --- Separator / Line Break ---

  if (isVisualElement) {
    return (
      <div style={{
        padding: '4px 12px', borderRadius: 4, border: `1px dashed ${token.colorBorderSecondary}`,
        marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, backgroundColor: token.colorFillTertiary,
        minWidth: 0, overflow: 'hidden'
      }}>
        {onMove && (
          <Button.Group size="small" style={{ flexShrink: 0 }}>
            <Button icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onMove('up')} />
            <Button icon={<ArrowDownOutlined />} disabled={index === total - 1} onClick={() => onMove('down')} />
          </Button.Group>
        )}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
          {isSeparator ? (
            <>
              <Divider style={{ margin: 0, flex: 1, minWidth: 0 }} />
              <Text type="secondary" style={{ fontSize: 11, padding: '0 8px', whiteSpace: 'nowrap', flexShrink: 0 }}>separator</Text>
              <Divider style={{ margin: 0, flex: 1, minWidth: 0 }} />
            </>
          ) : (
            <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>↵ line break</Text>
          )}
        </div>
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={onDelete} style={{ flexShrink: 0 }} />
      </div>
    );
  }

  // --- Standard / Component answer (recursive at every depth) ---

  const otherSiblings = allSiblings.filter((a, i) => i !== index && a.type !== 'separator' && a.type !== 'linebreak');

  return (
    <div style={{
      padding: isTopLevel ? 12 : 8,
      borderRadius: 4,
      border: isTopLevel ? `1px solid ${token.colorBorder}` : `1px solid ${token.colorBorderSecondary}`,
      marginBottom: 8,
      backgroundColor: isTopLevel ? undefined : token.colorFillTertiary
    }}>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {/* Header: move · type toggle · actions */}
        <Row gutter={8} align="middle">
          {onMove && (
            <Col>
              <Button.Group size="small">
                <Button icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onMove('up')} />
                <Button icon={<ArrowDownOutlined />} disabled={index === total - 1} onClick={() => onMove('down')} />
              </Button.Group>
            </Col>
          )}
          <Col flex="auto">
            <Select
              size="small"
              value={isComponentType ? 'component' : 'standard'}
              onChange={switchMode}
              style={{ width: isTopLevel ? 160 : 110 }}
            >
              <Select.Option value="standard">{isTopLevel ? 'Standard Answer' : 'Standard'}</Select.Option>
              <Select.Option value="component">{isTopLevel ? 'Field Component' : 'Component'}</Select.Option>
            </Select>
          </Col>
          <Col>
            {isTopLevel && onClone ? (
              <ActionButtons onClone={onClone} onDelete={onDelete} size="small" cloneTitle="Clone answer" />
            ) : (
              <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={onDelete} />
            )}
          </Col>
        </Row>

        {/* Component mode */}
        {isComponentType ? (
          <>
            <Row gutter={8} align="middle">
              <Col flex="auto" style={{ maxWidth: isParametrable ? 'calc(100% - 32px)' : '100%' }}>
                <Row gutter={8}>
                  <Col span={10}>
                    <Select
                      size="small"
                      value={answer.componentType}
                      onChange={(val) => {
                        updateSchema((s) => {
                          const node = getNode(s);
                          if (!node) return;
                          node.componentType = val;
                          // Update config based on type
                          if (PARAMETRABLE_FIELD_TYPES.includes(val)) {
                            node.config = { ...(PARAMETRABLE_DEFAULTS[val] || {}), ...(node.config || {}) };
                          } else {
                            delete node.config;
                          }
                        });
                      }}
                      style={{ width: '100%' }}
                      placeholder="Field type"
                    >
                      {ANSWER_FIELD_TYPES.map(ft => (
                        <Select.Option key={ft.value} value={ft.value}>{ft.label}</Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={14}>
                    <DebouncedInput
                      size="small"
                      placeholder="Label"
                      value={answer.label}
                      onChange={(e) => updateField('label', e.target.value)}
                    />
                  </Col>
                </Row>
              </Col>
              {isParametrable && (
                <Col>
                  <Popover
                    content={renderParametrableSettings(answer.componentType, getConfig(), updateConfig)}
                    trigger="click"
                    open={settingsOpen}
                    onOpenChange={setSettingsOpen}
                    placement="bottomRight"
                  >
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<SettingOutlined />} 
                      style={{ color: '#8c8c8c' }}
                    />
                  </Popover>
                </Col>
              )}
            </Row>

            {/* Select / Radio options editor */}
            {hasOptions && (
              <div style={{ paddingLeft: 8, borderLeft: '2px solid #e8e8e8' }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                  Select Options
                </Text>
                {answer.options?.map((opt, oi) => (
                  <Row gutter={8} key={oi} align="middle" style={{ marginBottom: 4 }}>
                    <Col flex="auto">
                      <DebouncedInput
                        size="small"
                        placeholder={`Option ${oi + 1}`}
                        value={opt.label || ''}
                        onChange={(e) => updateOption(oi, e.target.value)}
                      />
                    </Col>
                    <Col>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeOption(oi)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={addOption}
                  block
                  style={{ fontSize: 11 }}
                >
                  Add Select Option
                </Button>
              </div>
            )}

            {/* Terminology for field components */}
            <Row gutter={8}>
              <Col span={10}>
                {renderTerminologySystemSelect(
                  answer.conceptSystem || DEFAULT_SNOMED_SYSTEM,
                  (value) => {
                    updateSchema((s) => {
                      const node = getNode(s);
                      if (!node) return;
                      node.conceptSystem = value;
                      node.conceptCode = '';
                      node.concept = '';
                      node.terminology = null;
                    });
                  },
                  'small'
                )}
              </Col>
              <Col span={14}>
                {renderTerminologySelect({
                  systemUrl: answer.conceptSystem || DEFAULT_SNOMED_SYSTEM,
                  value: answer.terminology?.id || answer.conceptCode || answer.concept || '',
                  ...(answer.terminology && { defaultValue: answer.terminology }),
                  onChange: (_, obj) => {
                    updateSchema((s) => {
                      const node = getNode(s);
                      if (!node) return;
                      if (obj) {
                        node.terminology = obj;
                        node.conceptCode = obj.code;
                        node.concept = obj.code;
                        if (obj.display && !node.label) node.label = obj.display;
                      } else {
                        node.terminology = null;
                        node.conceptCode = '';
                        node.concept = '';
                      }
                    });
                  },
                  size: 'small'
                })}
              </Col>
            </Row>
          </>
        ) : (
          /* Standard mode */
          <>
            <DebouncedInput
              placeholder="Answer label"
              value={answer.label}
              onChange={(e) => updateField('label', e.target.value)}
              addonBefore={isTopLevel ? 'Label' : undefined}
              size={isTopLevel ? 'middle' : 'small'}
            />
            <Row gutter={8}>
              <Col span={10}>
                {renderTerminologySystemSelect(answer.conceptSystem, handleSystem, 'small')}
              </Col>
              <Col span={14}>
                {renderTerminologySelect({
                  systemUrl: answer.conceptSystem,
                  value: answer.terminology?.id || answer.conceptCode || answer.concept,
                  ...(answer.terminology && { defaultValue: answer.terminology }),
                  onChange: (_, obj) => handleTerminology(obj),
                  size: 'small'
                })}
              </Col>
            </Row>
          </>
        )}

        {/* Children — recursive self-reference */}
        {answer.children?.length > 0 && (
          <div style={{ paddingLeft: 16, borderLeft: '2px solid #e8e8e8' }}>
            <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
              <Col flex="auto">
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {depth === 0 ? 'Child answers' : 'Nested answers'}
                </Text>
              </Col>
              <Col>
                <Select
                  size="small"
                  value={answer.childrenLayout || 'vertical'}
                  onChange={(value) => updateField('childrenLayout', value)}
                  style={{ width: 90 }}
                >
                  <Select.Option value="vertical">Vertical</Select.Option>
                  <Select.Option value="horizontal">Horizontal</Select.Option>
                </Select>
              </Col>
              {answer.childrenLayout === 'horizontal' && (
                <Col>
                  <Select
                    size="small"
                    value={answer.childrenColumns || 'auto'}
                    onChange={(value) => {
                      updateSchema((s) => {
                        const node = getNode(s);
                        if (!node) return;
                        if (value === 'auto') {
                          delete node.childrenColumns;
                        } else {
                          node.childrenColumns = parseInt(value, 10);
                        }
                      });
                    }}
                    style={{ width: 80 }}
                    placeholder="Cols"
                  >
                    <Select.Option value="auto">Auto</Select.Option>
                    <Select.Option value="2">2 Cols</Select.Option>
                    <Select.Option value="3">3 Cols</Select.Option>
                    <Select.Option value="4">4 Cols</Select.Option>
                  </Select>
                </Col>
              )}
            </Row>
            {answer.children.map((child, ci) => (
              <AnswerEditor
                key={ci}
                answer={child}
                depth={depth + 1}
                index={ci}
                total={answer.children.length}
                getNode={(s) => getNode(s)?.children?.[ci]}
                updateSchema={updateSchema}
                onDelete={() => updateSchema((s) => { getNode(s)?.children?.splice(ci, 1); })}
                onClone={() => updateSchema((s) => {
                  const n = getNode(s);
                  if (n?.children?.[ci]) n.children.splice(ci + 1, 0, deepClone(n.children[ci]));
                })}
                onMove={(dir) => updateSchema((s) => {
                  const arr = getNode(s)?.children;
                  if (!arr) return;
                  const ni = dir === 'up' ? ci - 1 : ci + 1;
                  if (ni < 0 || ni >= arr.length) return;
                  [arr[ci], arr[ni]] = [arr[ni], arr[ci]];
                })}
              />
            ))}
          </div>
        )}

        {/* Add child — available on standard answers at any depth */}
        {!isComponentType && (
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={addChild}
            style={{ fontSize: 11, color: '#8c8c8c', padding: '0 4px' }}
          >
            Add child
          </Button>
        )}

        {/* Conditional Display — top-level only */}
        {isTopLevel && otherSiblings.length > 0 && (
          <Collapse ghost size="small">
            <Panel header={<Text style={{ fontSize: 11 }}>Conditional Display</Text>} key="1">
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Show this answer only when another answer is selected:
                </Text>
                <Row gutter={8}>
                  <Col span={10}>
                    <Select
                      placeholder="Source answer"
                      value={answer.enableWhen?.answerIndex}
                      onChange={(val) => {
                        updateSchema((s) => {
                          const node = getNode(s);
                          if (!node) return;
                          node.enableWhen = {
                            answerIndex: val,
                            operator: node.enableWhen?.operator || '=',
                            answerCoding: node.enableWhen?.answerCoding || ''
                          };
                        });
                      }}
                      allowClear
                      onClear={() => updateField('enableWhen', null)}
                      style={{ width: '100%' }}
                      size="small"
                    >
                      {otherSiblings.map((ans) => {
                        const actualIndex = allSiblings.findIndex(a => a === ans);
                        return (
                          <Select.Option key={actualIndex} value={actualIndex}>
                            {ans.label || `Answer ${actualIndex + 1}`}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Col>
                  <Col span={6}>
                    <Select
                      size="small"
                      placeholder="Operator"
                      value={answer.enableWhen?.operator || '='}
                      onChange={(val) => {
                        updateSchema((s) => {
                          const node = getNode(s);
                          if (!node) return;
                          if (!node.enableWhen) node.enableWhen = { answerIndex: 0 };
                          node.enableWhen.operator = val;
                        });
                      }}
                      style={{ width: '100%' }}
                    >
                      <Select.Option value="=">=</Select.Option>
                      <Select.Option value="!=">≠</Select.Option>
                      <Select.Option value="exists">exists</Select.Option>
                      <Select.Option value="!exists">not exists</Select.Option>
                    </Select>
                  </Col>
                  <Col span={8}>
                    <DebouncedInput
                      size="small"
                      placeholder="Value"
                      value={answer.enableWhen?.answerCoding || ''}
                      onChange={(e) => {
                        updateSchema((s) => {
                          const node = getNode(s);
                          if (!node) return;
                          if (!node.enableWhen) node.enableWhen = { answerIndex: 0 };
                          node.enableWhen.answerCoding = e.target.value;
                        });
                      }}
                    />
                  </Col>
                </Row>
              </Space>
            </Panel>
          </Collapse>
        )}
      </Space>
    </div>
  );
};
